import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { 
  PoButtonModule, 
  PoContainerModule, 
  PoProgressModule, 
  PoPageModule,
  PoInfoModule,
  PoIconModule,
  PoTagModule,
  PoFieldModule
} from '@po-ui/ng-components';
import { StepBreadcrumbComponent, StepBreadcrumbItem } from '../../../shared/components/step-breadcrumb/step-breadcrumb.component';
import { ScannerComponent } from '../../../shared/components/scanner/scanner.component';
import { AudioService } from '../../../shared/service/audio.service';
import { ToasterComponent } from '../../../shared/components/toaster/toaster.component';
import { ToasterService } from '../../../shared/service/toaster.service';
import { PoTagType } from '@po-ui/ng-components';
import { GeneralCountService } from '../../../shared/service/general-count.service';
import { TokenService } from '../../../shared/service/token.service';

interface LooseConferenceItem {
  number: string;
  description: string;
  conferred: number;
  batchBarCode: string;
  productCode: number;
  barCode: string;
}

@Component({
  selector: 'app-loose-conference',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PoButtonModule,
    PoContainerModule,
    PoProgressModule,
    PoPageModule,
    PoInfoModule,
    PoIconModule,
    PoTagModule,
    PoFieldModule,
    ScannerComponent,
    StepBreadcrumbComponent,
    ToasterComponent
  ],
  templateUrl: './loose-conference.component.html',
  styleUrl: './loose-conference.component.css'
})
export class LooseConferenceComponent implements OnInit {
  @Input() queryParamsInput: any;
  
  private readonly BEEP_SUCCESS = 3;
  private readonly BEEP_WARNING = 4;
  private readonly BEEP_ERROR = 5;

  private readonly DEFAULT_PRODUCT_QUANTITY = 1;
  private readonly INPUT_FOCUS_DELAY = 100;

  branchCode: number = 0;
  countCode: number = 0;
  countDescription: string = '';
  createdDate: string = '';
  countStatus: string = '';
  isFinished: boolean = false;
  inputQuantity: number = this.DEFAULT_PRODUCT_QUANTITY;
  productQuantity: number = this.DEFAULT_PRODUCT_QUANTITY;
  
  private allItems: LooseConferenceItem[] = [];
  
  @ViewChild(ScannerComponent) scannerComponent!: ScannerComponent;
  @ViewChild(ToasterComponent) toasterComponent!: ToasterComponent;

  readonly breadcrumbs: StepBreadcrumbItem[] = [
    { label: 'Home', id: '/home' },
    { label: 'Contagem Avulsa', id: '/avulsa' },
    { label: 'Conferência', id: '' }
  ];

  orderCountStep = 2;

  get itemsConferidos(): LooseConferenceItem[] {
    return this.allItems;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toasterService: ToasterService,
    private audioService: AudioService,
    private generalCountService: GeneralCountService,
    private tokenService: TokenService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      // Prioriza queryParamsInput se existir, senão usa params da rota
      const sourceParams = this.queryParamsInput || params;
      
      this.countCode = Number(sourceParams['countCode']) || 0;
      this.countDescription = sourceParams['description'] || '';
      this.createdDate = sourceParams['createdDate'] || '';
      this.countStatus = sourceParams['conferenceStatus'] || '';
      this.branchCode = Number(this.tokenService.getBranch()) || 0;
      
      this.isFinished = this.countStatus === 'closed' || 
                       this.countStatus === 'finished' || 
                       this.countStatus === 'canceled' || 
                       this.countStatus === 'answered' || 
                       this.countStatus === 'grouped';
      
      if (this.isFinished) {
        const statusInfo = this.getConferenceStatusInfo(this.countStatus);
      }
      
      this.loadGeneralCountItems();
    });
  }

  private loadGeneralCountItems(): void {
    if (!this.countCode || !this.branchCode) {
      return;
    }

    this.generalCountService.getGeneralCountItems(this.branchCode, this.countCode).subscribe({
      next: (response: any) => {
        if (response?.items) {
          this.mapGeneralCountItems(response.items);
        }
      },
      error: (error) => {
        this.handleError(error, 'Erro ao carregar itens da contagem.');
      }
    });
  }

  private mapGeneralCountItems(items: any[]): void {
    this.allItems = items.map((item: any) => {
      return {
        number: item.productCode?.toString() || '',
        description: item.productDescription || item.productName || 'Carregando...',
        conferred: item.countQuantity || 0,
        batchBarCode: item.batchBarCode || '',
        productCode: item.productCode || 0,
        barCode: item.productBarCode || ''
      };
    });
    
    this.loadProductDescriptions();
  }

  private loadProductDescriptions(): void {
    this.allItems.forEach((item, index) => {
      if (item.productCode && item.description === 'Carregando...') {
        this.generalCountService.getProduct(item.productCode.toString(), this.branchCode).subscribe({
          next: (productResponse: any) => {
            if (productResponse?.items && productResponse.items.length > 0) {
              const product = productResponse.items[0];
              this.allItems[index].description = product.productName || product.productDescription || 'Sem descrição';
            }
          },
          error: (error) => {
            this.allItems[index].description = 'Sem descrição';
          }
        });
      }
    });
  }

  validateInputQuantity(): void {
    if (this.inputQuantity < 1) {
      this.inputQuantity = 1;
    }
    this.inputQuantity = Math.floor(this.inputQuantity);
  }

  onScannedData(barcode: string): void {
    if (!barcode) return;

    if (this.isFinished) {
      this.audioService.playBeep(this.BEEP_ERROR);
      this.toasterService.triggerAlert('Contagem finalizada - Não é possível adicionar itens', 'warning');
      return;
    }

    this.validateInputQuantity();

    this.generalCountService.getProduct(barcode, this.branchCode).subscribe({
      next: (productResponse: any) => {
        if (productResponse?.items && productResponse.items.length > 0) {
          const product = productResponse.items[0];
          
          if (product.isBatchItem) {
            this.productQuantity = product.quantity || this.DEFAULT_PRODUCT_QUANTITY;
          } else {
            this.productQuantity = this.inputQuantity;
          }
          
          this.handleBarcodeScanned(barcode, product);
        } else {
          this.showProductNotFoundError();
        }
      },
      error: () => {
        this.showProductNotFoundError();
      }
    });
  }

  private handleBarcodeScanned(barcode: string, product: any): void {
    if (product.isBatchItem) {
      this.processConferenceItem(barcode, product);
    } else {
      this.processConferenceItem(product.productCode?.toString() || barcode, product);
    }
  }

  private processConferenceItem(barcode: string, product: any): void {
    let countItem = this.allItems.find(c => c.productCode === product.productCode);
    
    if (!countItem && product.isBatchItem) {
      countItem = this.allItems.find(c => c.batchBarCode && c.batchBarCode === barcode.toString());
    }
    
    if (!countItem) {
      countItem = this.allItems.find(c => c.number === product.productCode?.toString());
    }

    if (countItem) {
      this.updateExistingItem(countItem, barcode, product);
    } else {
      this.addNewItem(product.productCode, barcode, product);
    }
  }

  private updateExistingItem(countItem: LooseConferenceItem, barcode: string, product: any): void {
    this.generalCountService.postGeneralCountItem(
      this.branchCode,
      this.countCode,
      countItem.productCode.toString(),
      barcode,
      this.productQuantity
    ).subscribe({
      next: () => {
        countItem.conferred += this.productQuantity;
        countItem.description = product.productName || product.productDescription || countItem.description;
        countItem.number = product.productCode?.toString() || countItem.number;
        
        if (product.isBatchItem) {
          countItem.batchBarCode = barcode;
        }
        
        this.playSuccessBeep();
        
        if (product.isBatchItem) {
          this.toasterService.triggerAlert(`Item de lote atualizado (Qtd: ${this.productQuantity})`, 'success');
        }
        
        this.productQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.inputQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.focusScannerInput();
      },
      error: (error) => {
        this.handleError(error, 'Erro ao atualizar item.');
        this.audioService.playBeep(this.BEEP_ERROR);
        this.productQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.focusScannerInput();
      }
    });
  }

  private addNewItem(productCode: number, barcode: string, product: any): void {
    this.generalCountService.postGeneralCountItem(
      this.branchCode,
      this.countCode,
      productCode.toString(),
      barcode,
      this.productQuantity
    ).subscribe({
      next: () => {
        const newItem: LooseConferenceItem = {
          number: product.productCode?.toString() || productCode.toString(),
          description: product.productName || product.productDescription || 'Sem descrição',
          conferred: this.productQuantity,
          batchBarCode: product.isBatchItem ? barcode : '',
          productCode: productCode,
          barCode: barcode
        };
        
        this.allItems.push(newItem);
        
        this.playSuccessBeep();
        
        if (product.isBatchItem) {
          this.toasterService.triggerAlert(`Item de lote adicionado (Qtd: ${this.productQuantity})`, 'success');
        }
        
        this.productQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.inputQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.focusScannerInput();
      },
      error: (error) => {
        this.handleError(error, 'Erro ao adicionar item.');
        this.audioService.playBeep(this.BEEP_ERROR);
        this.productQuantity = this.DEFAULT_PRODUCT_QUANTITY;
        this.focusScannerInput();
      }
    });
  }

  removeItem(item: LooseConferenceItem): void {
    if (this.isFinished) {
      this.toasterService.triggerAlert('Contagem finalizada - Não é possível remover itens', 'warning');
      return;
    }

    this.generalCountService.deleteGeneralCountItem(
      this.branchCode,
      this.countCode,
      item.productCode,
      item.barCode
    ).subscribe({
      next: () => {
        const index = this.allItems.indexOf(item);
        if (index > -1) {
          this.allItems.splice(index, 1);
        }
        this.toasterService.triggerAlert('Item ' + item.productCode + ' removido com sucesso', 'success');
      },
      error: (error) => {
        this.handleError(error, 'Erro ao remover item.');
      }
    });
  }

  getTotalQuantity(): number {
    return this.allItems.reduce((total, item) => total + Number(item.conferred), 0);
  }

  finishCount(): void {
    if (this.isFinished) {
      this.toasterService.triggerAlert('Esta contagem já foi finalizada', 'warning');
      return;
    }

    if (this.allItems.length === 0) {
      this.toasterService.triggerAlert('Adicione pelo menos um item', 'warning');
      return;
    }

    this.generalCountService.postGeneralCountStatus(this.branchCode, this.countCode, 5).subscribe({
      next: () => {
        this.toasterService.triggerAlert('Contagem finalizada com sucesso', 'success');
        setTimeout(() => {
          this.router.navigate(['/avulsa']);
        }, 1500);
      },
      error: (error) => {
        this.handleError(error, 'Erro ao finalizar contagem.');
      }
    });
  }

  navigateBreadcrumb(event: { item: StepBreadcrumbItem; index: number }): void {
    if (event.item.id) {
      this.router.navigate([event.item.id]);
    }
  }

  private showProductNotFoundError(): void {
    this.audioService.playBeep(this.BEEP_ERROR);
    this.toasterService.triggerAlert('Produto não encontrado.', 'error');
    this.focusScannerInput();
  }

  private playSuccessBeep(): void {
    this.audioService.playBeep(this.BEEP_SUCCESS);
  }

  private focusScannerInput(): void {
    setTimeout(() => {
      const inputElement = document.querySelector('input[name="inputBarcode"]') as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    }, this.INPUT_FOCUS_DELAY);
  }

  private handleError(error: any, defaultMessage: string): void {
    const errorMessage = error?.error?.[0]?.message || defaultMessage;
    this.toasterService.triggerAlert(errorMessage, 'error');
  }

  getConferenceStatusInfo(status: string) {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'closed' || normalized === 'finished') {
      return { text: 'Contagem finalizada', icon: 'po-icon-ok', class: 'conference-status-closed' };
    }
    if (normalized === 'answered') {
      return { text: 'Contagem respondida', icon: 'po-icon-ok', class: 'conference-status-answered' };
    }
    if (normalized === 'canceled') {
      return { text: 'Contagem cancelada', icon: 'po-icon-close', class: 'conference-status-canceled' };
    }
    if (normalized === 'grouped') {
      return { text: 'Contagem agrupada', icon: 'po-icon-doc', class: 'conference-status-grouped' };
    }
    return { text: 'Em andamento', icon: 'po-icon-refresh', class: 'conference-status-in-progress' };
  }
}
